import axios, { type AxiosResponse } from "axios";
import {
  AccountResultSchema,
  AccountItemSchema,
  AccountLicenseItemSchema,
  BusinessUnitResultSchema,
  EmployeeResultSchema,
} from "@/schemas/index";
import type {
  AccountResultType,
  AccountItemType,
  AccountLicenseItemType,
  BusinessUnitResultType,
  EmployeeResultType,
} from "@/schemas/index";
import env from "@/app/config/env";

export interface ServiceAccountsApiType {
  fetchAccounts: (
    pageIndex?: number,
    pageSize?: number,
  ) => Promise<AccountResultType>;
  fetchAccountById: (id: AccountItemType["Id"]) => Promise<AccountItemType>;

  fetchAccountEmployeesById: (
    id: AccountItemType["Id"],
    params?: {
      pageIndex?: number;
      pageSize?: number;
    },
  ) => Promise<EmployeeResultType>;

  fetchAccountBusinessUnitsById: (
    id: AccountItemType["Id"],
    params?: {
      query?: string;
      pageIndex?: number;
      pageSize?: number;
    },
  ) => Promise<BusinessUnitResultType>;

  fetchAccountLicensesById: (
    id: AccountItemType["Id"],
  ) => Promise<AccountLicenseItemType[]>;

  fetchUpdateAccount: (data: Partial<AccountItemType>) => Promise<void>;
  fetchCreateAccount: (
    data: Partial<AccountItemType>,
  ) => Promise<AccountItemType>;
}

export const serviceAccountsApi: ServiceAccountsApiType = {
  fetchAccounts: (pageIndex, pageSize) => {
    return axios
      .get(`${env.services.portal}/accounts`, {
        params: {
          pageIndex: pageIndex,
          pageSize: pageSize,
        },
      })
      .then((response: AxiosResponse<unknown>) => {
        return AccountResultSchema.parse(response.data);
      });
  },

  fetchAccountById: (id) => {
    return axios
      .get(`${env.services.portal}/accounts/${id}`)
      .then((response: AxiosResponse<unknown>) => {
        return AccountItemSchema.parse(response.data);
      });
  },

  fetchAccountEmployeesById: (id, params) => {
    return axios
      .get(`${env.services.portal}/accounts/${id}/employees`, { params })
      .then((response: AxiosResponse<unknown>) => {
        return EmployeeResultSchema.parse(response?.data);
      });
  },

  fetchAccountBusinessUnitsById: (id, params) => {
    return axios
      .get(`${env.services.portal}/accounts/${id}/businessunits`, { params })
      .then((response: AxiosResponse<unknown>) => {
        return BusinessUnitResultSchema.parse(response?.data);
      });
  },

  fetchAccountLicensesById: (id) => {
    return axios
      .get(`${env.services.portal}/accounts/${id}/licenses`)
      .then((response: AxiosResponse<unknown[]>) => {
        return response?.data?.map((item) =>
          AccountLicenseItemSchema.parse(item),
        );
      });
  },

  fetchUpdateAccount: (data) =>
    axios.patch(`${env.services.portal}/accounts/${data.Id}`, data),

  fetchCreateAccount: (data) => {
    return axios
      .post(`${env.services.portal}/accounts`, data)
      .then((response: AxiosResponse<unknown>) => {
        return AccountItemSchema.parse(response.data);
      });
  },
};
